/**
 * SEO Structured Data (JSON-LD) for Practido Electronics Fun Kit Manual
 * Follows schema.org standards for FAQPage, HowTo, and EducationalOrganization.
 */

export const electronicsKitFaqSchema = [
  {
    question: "What is the Practido Electronics Fun Kit?",
    answer:
      "The Practido Electronics Fun Kit is a beginner-friendly STEM electronics kit that lets kids build 15 solder-free projects using simple household items, a 9V battery, LEDs, buzzer, motor, and push buttons.",
  },
  {
    question: "Do I need to solder to build projects with this kit?",
    answer:
      "No soldering is required! All joints are securely connected using the 'Twist & Tape' method: cross wire ends, twist tightly 3-4 times, and seal with tape.",
  },
  {
    question: "Why does my LED not light up?",
    answer:
      "LEDs only work one way round. Turn the LED around: the long leg is positive (+) and must connect towards the red wire, while the short leg is negative (−) and connects towards the black wire.",
  },
  {
    question: "Why do I always need a resistor with an LED on a 9V battery?",
    answer:
      "A 9V battery provides more voltage than a small LED can handle on its own. A resistor limits the current so the LED does not burn out.",
  },
  {
    question: "How do I correctly connect the push button?",
    answer:
      "Use two legs at opposite diagonal corners (one top, one bottom, on different sides). This ensures the switch properly opens and closes the circuit every time.",
  },
  {
    question: "What should I do if the 9V battery feels warm?",
    answer:
      "Unclip the battery immediately! A warm battery indicates a short circuit, meaning the red and black wires are touching directly somewhere.",
  },
  {
    question: "Why does the motor hum but not spin?",
    answer:
      "Give the fan blade a gentle flick with your finger and check that the blade is not rubbing against the cup rim, tape, or surface.",
  },
  {
    question: "How can I reverse the direction of the desk fan?",
    answer:
      "Simply swap the red and black wires between the two motor terminals: DC motors rotate in the opposite direction when polarity is reversed.",
  },
  {
    question: "What age group is the Electronics Fun Kit suitable for?",
    answer:
      "The kit is designed for children aged 6 to 16, with adult supervision recommended when using scissors and pins.",
  },
];

export const electronicsKitHowToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to Build 15 Solder-Free Projects with the Practido Electronics Fun Kit",
  description:
    "Step-by-step instructions for kids to build 15 fun electronics circuits—from a glowing Rocket Card and Doorbell to a Morse Code machine and Drawing Robot—without any soldering.",
  totalTime: "PT3H",
  estimatedCost: {
    "@type": "MonetaryAmount",
    currency: "INR",
    value: "0",
  },
  supply: [
    { "@type": "HowToSupply", name: "9V battery and connector clip" },
    { "@type": "HowToSupply", name: "Red, Yellow, and Green LEDs" },
    { "@type": "HowToSupply", name: "Protective resistors" },
    { "@type": "HowToSupply", name: "Buzzer" },
    { "@type": "HowToSupply", name: "Push button" },
    { "@type": "HowToSupply", name: "DC Toy Motor and fan blade" },
    { "@type": "HowToSupply", name: "Kitchen foil and paper clips" },
  ],
  tool: [
    { "@type": "HowToTool", name: "Tape (Sellotape or electrical tape)" },
    { "@type": "HowToTool", name: "Scissors" },
    { "@type": "HowToTool", name: "Glue stick" },
  ],
  step: [
    {
      "@type": "HowToStep",
      name: "Master the 3 Golden Rules",
      text: "Learn LED polarity (long leg = +, short leg = −), push button diagonal pinout, and the twist & tape joint method.",
      url: "https://practido.com/robotics-fun-kit#safety-basics",
    },
    {
      "@type": "HowToStep",
      name: "Build the Rocket Card",
      text: "Lay down kitchen foil tracks on the printed card, insert the LED, position the coin battery, and fold to light up the rocket.",
      url: "https://practido.com/robotics-fun-kit#project-1",
    },
    {
      "@type": "HowToStep",
      name: "Assemble Basic Light & Sound Circuits",
      text: "Construct My First Light, Magic Light Switch, and Doorbell with 9V power, resistors, and momentary switches.",
      url: "https://practido.com/robotics-fun-kit#projects",
    },
    {
      "@type": "HowToStep",
      name: "Construct Advanced Mechanics & Sensors",
      text: "Create the Traffic Signal rotary switch, Steady Hand wire loop game, Rain Detector, Drawing Robot, and Door Alarm.",
      url: "https://practido.com/robotics-fun-kit#projects",
    },
  ],
};

export const electronicsKitOrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "EducationalOrganization",
  name: "Practido",
  url: "https://practido.com",
  logo: "https://practido.com/img/logo_full.png",
  description:
    "Practido is India's premier e-learning and STEM education platform offering interactive apps, robotics and AI classes, and hands-on electronics kits.",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9560734660",
    contactType: "customer support",
    availableLanguage: ["English", "Hindi"],
  },
};
